import { RefObject, useEffect } from 'react'

/** Set on `<html>` while a surface wants the heavier pull. Read by components/PullToRefresh. */
export const LONG_PULL_ATTRIBUTE = 'data-long-pull-refresh'

/**
 * Set on the element a surface scrolls its content in, while that surface is the one on
 * screen. Read by components/PullToRefresh, which refuses to arm while a reported pane is
 * scrolled away from its own top.
 */
export const PULL_SCROLL_PANE_ATTRIBUTE = 'data-pull-scroll-pane'

/**
 * Asks for a longer, more deliberate pull before pull-to-refresh arms on this surface.
 *
 * AIM on mobile is the case this exists for. The default threshold is calibrated for a
 * page that scrolls the document: PullToRefresh only arms at `scrollY === 0`, so anywhere
 * else the gesture is read as a scroll and never becomes a pull. AIM's conversation list
 * scrolls inside its own pane (`.sidebarScroll`), which leaves the document at scroll 0
 * for the whole list — so a swipe down the list is, to that check, indistinguishable from
 * a pull at the top of a page, and an accidental refresh throws away where you were in it.
 *
 * The pane check in PullToRefresh keeps a swipe on a scrolled list from arming at all; this
 * covers what is left, the swipe that starts with the list already at its top, by asking for
 * travel no light or glancing gesture produces.
 *
 * Published as an attribute on `<html>` rather than passed as a prop for the same reason
 * as `data-immersive-surface` (useImmersiveSurface) and `data-keyboard-open`
 * (useVisualViewport): PullToRefresh is mounted by MainLayout, nowhere near the page that
 * knows how its own content scrolls.
 *
 * @param isLongPull whether this surface is on screen AND the viewport is one where the
 * gesture exists at all — pass the caller's `isMobile` check in, since PullToRefresh
 * no-ops above `md`.
 * @param paneRef the element this surface scrolls its content in, if it has one. Worth
 * passing: PullToRefresh's other pane check walks up from whatever the finger touched, so
 * it only answers for a finger that landed inside the pane. AIM's sidebar header and filter
 * field sit above `.sidebarScroll`, and a pull started on that chrome walks straight past it.
 * Reporting the pane makes the guard independent of where in the surface the touch landed.
 */
const useLongPullToRefresh = (
  isLongPull: boolean,
  paneRef?: RefObject<HTMLElement | null>,
): void => {
  useEffect(() => {
    if (!isLongPull) {
      return
    }

    document.documentElement.setAttribute(LONG_PULL_ATTRIBUTE, '')
    const pane = paneRef?.current
    pane?.setAttribute(PULL_SCROLL_PANE_ATTRIBUTE, '')

    // Always removed on the way out, so leaving the surface — or crossing the md
    // breakpoint — puts the rest of the app back on the ordinary threshold. The pane
    // attribute goes with it, which is what keeps a scrolled sidebar in the embedded
    // bubble from blocking a pull on the page behind it.
    return () => {
      document.documentElement.removeAttribute(LONG_PULL_ATTRIBUTE)
      pane?.removeAttribute(PULL_SCROLL_PANE_ATTRIBUTE)
    }
  }, [isLongPull, paneRef])
}

export default useLongPullToRefresh
